import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import Seo from './Seo';
import { PageHead, SectionHead, Specimen, RateCard, Facts, WhereNext } from './Editorial';
import { LINKEDIN } from '../data/company';
import LabDial from './LabDial';
import './main.css';

/* Fig. 1 — an illustrative auth log, annotated the way Vigilhold explains
   events. Addresses are from the RFC 5737 documentation ranges. */
const NIGHT_LOG = [
  { text: 'Sep 22 03:14:02 web-01 sshd[41187]: Failed password for root from 203.0.113.42 port 52144 ssh2' },
  { text: 'Sep 22 03:14:03 web-01 sshd[41187]: Failed password for root from 203.0.113.42 port 52144 ssh2' },
  { text: 'Sep 22 03:14:05 web-01 sshd[41190]: Invalid user admin from 203.0.113.42 port 52198' },
  { text: '… 37 more from 203.0.113.42 — oracle, ubuntu, test, git, postgres …', muted: true },
  {
    text: 'Sep 22 03:15:41 web-01 sshd[41302]: Failed password for invalid user deploy from 203.0.113.42 port 53011 ssh2',
    note: 'One address, forty guesses in under two minutes, working down a list of common usernames. A brute-force attempt, not someone who forgot a password.',
  },
  {
    text: 'Sep 22 03:15:42 web-01 kernel: [UFW BLOCK] IN=eth0 SRC=203.0.113.42 DST=198.51.100.10 PROTO=TCP DPT=22',
    note: 'The address gets a temporary ban, automatically. That is the one kind of action Assist mode takes on its own.',
    sub: 'Logged, and reversible.',
  },
  {
    text: 'Sep 22 03:15:58 web-01 kernel: audit: EXECVE pid=51877 uid=33 (www-data) exe="/tmp/.x/kworkerd"',
    note: 'A program started from /tmp by the web server’s user. Stopping it could break something, so Vigilhold asks you first.',
    sub: 'One click to approve. Nothing disruptive happens without you.',
  },
];

const SIGNALS = [
  { name: 'Process', gloss: 'what started, and as whom' },
  { name: 'Network', gloss: 'who connected, and to where' },
  { name: 'File', gloss: 'what changed on disk' },
  { name: 'SSH', gloss: 'who tried to log in' },
];

/* What each autonomy level hands to the agent and what it keeps for you,
   per Vigilhold's learning-mode rules. */
const MODES = [
  {
    id: 'observe', name: 'Observe',
    handled: 'Nothing. Each response is simulated and logged instead, so you see what it would have done.',
    waiting: 'Every action, IP bans included.',
  },
  {
    id: 'assist', name: 'Assist',
    handled: 'Temporary IP bans against an address that is actively attacking you.',
    waiting: 'Killing a process, isolating or locking down a server: one-click approval.',
  },
  {
    id: 'autopilot', name: 'Autopilot',
    handled: 'The responses in your policy run on their own, from IP bans to stopping a flagged process.',
    waiting: 'Only what your policy still reserves for approval.',
  },
];

const NEXT = [
  { label: 'Products', to: '/products' },
  { label: 'About', to: '/about' },
  { label: 'Trust', to: '/trust' },
  { label: 'Blog', to: '/blog' },
];

export default function Main() {
  const [modeId, setModeId] = useState('assist');
  const mode = MODES.find(m => m.id === modeId);
  return (
    <div className="page">
      <Header />
      <Seo
        title="Al's-Ops LLC — Security Software & the Vigilhold Host Agent"
        description="Vigilhold, from Al's-Ops LLC, watches process, network, file and SSH activity on Linux, Windows, Docker and Kubernetes hosts. Verified accounts from $39/mo."
        canonical="https://alsopss.com/"
        keywords={["Al's-Ops LLC","Vigilhold host agent","Linux host security","Windows host security","Docker security agent","Kubernetes security agent","host-based intrusion detection","zero-trust Linux","server security platform","SOC2 compliance automation","Utah software company"]}
        jsonLd={{"@context":"https://schema.org","@type":"ItemList","name":"Al's-Ops LLC Products","url":"https://alsopss.com/products","itemListElement":[{"@type":"ListItem","position":1,"name":"Vigilhold","url":"https://vigilhold.com","description":"Host agent for Linux, Windows, Docker, and Kubernetes. Watches process, network, file, and SSH signals and takes only the actions you configure. Verified accounts from $39/month, 14-day free trial."},{"@type":"ListItem","position":2,"name":"Thunderstorm VPN","url":"https://alsopss.com/products","description":"Privacy-focused VPN in development."},{"@type":"ListItem","position":3,"name":"spd-lib","url":"https://www.npmjs.com/package/spd-lib","description":"Open-source post-quantum-hardened encrypted data format for Node.js, browsers, Deno, and Bun."}]}}
      />

      <main id="main-content">
        <PageHead
          meta="Al's-Ops LLC · Security and infrastructure software · Eagle Mountain, Utah"
          title={<>What happened on your server at 3:14&nbsp;a.m. <span className="soft">In plain language.</span></>}
          lede="Vigilhold watches process, network, file and SSH signals on Linux, Windows, Docker and Kubernetes, then tells you what happened, why it matters, and acts only within the limits you set."
        >
          <div className="head-actions">
            <div className="actions">
              <a href="https://vigilhold.com/register" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                Start a 14-day trial <span aria-hidden="true">↗</span>
              </a>
              <a href="https://vigilhold.com/lab" className="link-arrow" target="_blank" rel="noopener noreferrer">
                See the live lab <span aria-hidden="true">↗</span>
              </a>
            </div>
            <p className="label home-note">No card. No anonymous free tier. From $39 a month.</p>
          </div>
        </PageHead>

        <section className="wrap home-log" aria-label="Example: one night of an auth log">
          <Specimen
            label="Example auth log with annotations"
            lines={NIGHT_LOG}
            caption={<><b>Fig. 1</b> — One night of an auth log, annotated the way Vigilhold explains events. Illustrative; addresses are from the documentation ranges.</>}
          />
        </section>

        <section className="section" aria-labelledby="decides-heading">
          <div className="wrap">
            <SectionHead mark="01 — How it decides" id="decides-heading" title="Four signals in, two kinds of outcome">
              New accounts start in Assist mode. The agent checks every signal against your policy and splits what it
              finds into what it handles on its own and what waits for you.
            </SectionHead>

            <figure className="dusk-plate flow-fig">
              <div className="mode-switch" role="group" aria-label="Autonomy mode">
                {MODES.map(m => (
                  <button key={m.id} type="button" aria-pressed={m.id === modeId} onClick={() => setModeId(m.id)}>
                    {m.name}{m.id === 'assist' && <span className="mode-default"> (default)</span>}
                  </button>
                ))}
              </div>
              <div className="flow-labels" aria-hidden="true">
                <span>Signals</span><span>On the host</span><span>Outcome</span>
              </div>
              <div className="flow">
                <ul className="flow-in" aria-label="Signals the agent watches">
                  {SIGNALS.map(s => (
                    <li key={s.name}>
                      <span className="flow-name">{s.name}</span>
                      <span className="flow-gloss">{s.gloss}</span>
                    </li>
                  ))}
                </ul>
                <span className="flow-arrow" aria-hidden="true">↓</span>
                <div className="flow-agent">
                  <h3>Vigilhold agent</h3>
                  <p>Checks every signal against your policy. If the control plane is unreachable it keeps the last-synced policy and records what it did.</p>
                </div>
                <span className="flow-arrow" aria-hidden="true">↓</span>
                <div className="flow-out" aria-live="polite">
                  <div className="flow-outcome handled">
                    <h3>Handled</h3>
                    <p>{mode.handled}</p>
                  </div>
                  <div className="flow-outcome waiting">
                    <h3>Waiting for you</h3>
                    <p>{mode.waiting}</p>
                  </div>
                </div>
              </div>
              <figcaption className="fig-cap">
                <b>Fig. 2</b> — {mode.name} mode. Switch modes to see how the split moves; you can change it at any time. Every action is logged and reversible.
              </figcaption>
            </figure>

            <div className="install">
              <p className="label">Install: one command per host, under a minute</p>
              <pre className="install-cmd"><code>curl -fsSL https://vigilhold.com/install | sudo bash -s -- --token &lt;token&gt;</code></pre>
            </div>
          </div>
        </section>

        <section className="section tinted" aria-labelledby="lab-heading">
          <div className="wrap lab-grid">
            <p className="lab-number" aria-hidden="true"><span>about</span>1,600</p>
            <div className="lab-text">
              <h2 id="lab-heading">Login attempts a day on a server nobody advertised.</h2>
              <p>
                Our public lab is an ordinary VPS. It gets about 1,600 login attempts a day from more than
                100 addresses: the background noise every server on the internet lives with.
              </p>
              <a href="https://vigilhold.com/lab" className="link-arrow" target="_blank" rel="noopener noreferrer">
                See today’s live count <span aria-hidden="true">↗</span>
              </a>
            </div>
            <LabDial fig={3} />
          </div>
        </section>

        <section className="section" aria-labelledby="pricing-heading">
          <div className="wrap">
            <SectionHead mark="02 — Pricing" id="pricing-heading" title="Three plans, priced per month">
              Developer for a handful of servers, Business for a fleet, Enterprise when you need custom deployments and SLAs.
            </SectionHead>
            <RateCard />
          </div>
        </section>

        <section className="section" aria-labelledby="about-heading">
          <div className="wrap grid-12 company" itemScope itemType="https://schema.org/Organization">
            <meta itemProp="name" content="Al's-Ops LLC" />
            <meta itemProp="foundingDate" content="2023" />
            <meta itemProp="url" content="https://alsopss.com" />

            <figure className="portrait company-portrait" itemScope itemType="https://schema.org/Person">
              <img src="/aiden-mcelroy.jpg" alt="Aiden McElroy, founder of Al's-Ops LLC" width="480" height="600" loading="lazy" itemProp="image" />
              <figcaption className="fig-cap">
                <span itemProp="name">Aiden McElroy</span>, <span itemProp="jobTitle">Founder &amp; CEO</span>. Eagle Mountain, Utah.
              </figcaption>
            </figure>

            <div className="company-text">
              <p className="label company-mark">03 — Who builds it</p>
              <h2 id="about-heading">A security company first. Patching after the breach isn’t a strategy.</h2>
              <div className="prose">
                <p itemProp="description">
                  Al's-Ops LLC was founded in 2023 by Aiden McElroy in Eagle Mountain, Utah. It is independent,
                  with no outside investors. Its mission is to build privacy-first security and infrastructure
                  software people can actually trust: software that stays useful when conditions are messy,
                  not just when everything is right.
                </p>
                <p>
                  Aiden leads product and engineering for Vigilhold, the company’s flagship host agent.
                  Consumer and experimental projects live separately under <Link to="/labs">AL'S-OPS Labs</Link>.
                </p>
              </div>
              <Facts
                label="Company facts"
                items={[
                  { label: 'Founded', value: '2023, Eagle Mountain, Utah' },
                  { label: 'Ownership', value: 'Independent, no outside investors' },
                  { label: 'Flagship', value: 'Vigilhold host agent' },
                  { label: 'Founder', value: <><Link to="/team/aiden-mcelroy">Aiden McElroy</Link>, <a href={LINKEDIN} target="_blank" rel="noopener noreferrer">LinkedIn ↗</a></> },
                ]}
              />
            </div>
          </div>
        </section>

        <WhereNext items={NEXT} />
      </main>
      <Footer />
    </div>
  );
}
